import {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

import ROUTES from '../config/routes';
import {
  getSectionIdsForPath,
  isMainRoute as isMainRouteHelper
} from '../config/navigation';

const NavigationContext = createContext(null);

export const NavigationProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [userHasToggledMenu, setUserHasToggledMenu] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);
  const [pendingNavigationAction, setPendingNavigationAction] = useState(null);

  const [activeNavId, setActiveNavId] = useState(null);
  const [expandedDesktopItemId, setExpandedDesktopItemId] = useState(null);

  const prevPathnameRef = useRef(location.pathname);
  const observerRef = useRef(null);
  const sectionsVisibilityRef = useRef({});

  const getParentPath = path => {
    if (!path || path === '/') return '/';
    const segments = path.split('/').filter(Boolean);
    if (segments.length === 0) return '/';
    return `/${segments[0]}`;
  };

  const isMainRoute = useCallback(path => {
    return isMainRouteHelper(path);
  }, []);

  // Intersection Observer for scroll tracking
  useEffect(() => {
    const sectionIds = getSectionIdsForPath(location.pathname);

    if (sectionIds.length === 0) {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      setActiveSection(null);
      return;
    }

    const observeElements = () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      const handleIntersection = entries => {
        entries.forEach(entry => {
          const sectionId = entry.target.id;
          sectionsVisibilityRef.current[sectionId] = {
            isIntersecting: entry.isIntersecting,
            ratio: entry.intersectionRatio,
            top: entry.boundingClientRect.top
          };
        });

        // Find the most visible section
        let mostVisibleSection = null;
        let highestRatio = 0;
        let closestToTop = Infinity;

        Object.keys(sectionsVisibilityRef.current).forEach(id => {
          const section = sectionsVisibilityRef.current[id];

          if (section.isIntersecting) {
            if (section.ratio > highestRatio) {
              mostVisibleSection = id;
              highestRatio = section.ratio;
              closestToTop = section.top;
            } else if (
              section.ratio === highestRatio &&
              section.top < closestToTop
            ) {
              mostVisibleSection = id;
              closestToTop = section.top;
            }
          }
        });

        // If no section is visible, find the closest one above viewport
        if (!mostVisibleSection) {
          let closestAbove = null;
          let closestDistance = Infinity;

          Object.keys(sectionsVisibilityRef.current).forEach(id => {
            const section = sectionsVisibilityRef.current[id];
            if (section.top < 0 && Math.abs(section.top) < closestDistance) {
              closestAbove = id;
              closestDistance = Math.abs(section.top);
            }
          });

          mostVisibleSection = closestAbove || sectionIds[0];
        }

        // Update active section
        if (mostVisibleSection) {
          setActiveSection(mostVisibleSection);
        }
      };

      observerRef.current = new IntersectionObserver(handleIntersection, {
        root: null,
        rootMargin: '-100px 0px -60% 0px', // Account for header height
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0]
      });

      sectionIds.forEach(id => {
        const element = document.getElementById(id);
        if (element) {
          observerRef.current.observe(element);
        }
      });
    };

    // Delay to ensure DOM is ready
    setTimeout(observeElements, 500);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [location.pathname]);

  // Page transition handler (must be defined before navigateToSection)
  const startPageTransitionV2 = useCallback(
    (navigateActionCallback, shouldAnimate = true) => {
      if (shouldAnimate && !isPageTransitioning) {
        setIsPageTransitioning(true);
        setPendingNavigationAction(() => navigateActionCallback);
      } else if (shouldAnimate && isPageTransitioning) {
        navigateActionCallback();
      } else {
        navigateActionCallback();
      }
    },
    [isPageTransitioning]
  );

  // Navigate to section helper
  const navigateToSection = useCallback(
    (sectionId, targetPath = null) => {
      const currentPath = location.pathname;
      const normalizedCurrentPath =
        currentPath !== '/' && currentPath.endsWith('/')
          ? currentPath.slice(0, -1)
          : currentPath;

      const normalizedTargetPath = targetPath
        ? targetPath !== '/' && targetPath.endsWith('/')
          ? targetPath.slice(0, -1)
          : targetPath
        : normalizedCurrentPath;

      if (normalizedTargetPath !== normalizedCurrentPath) {
        const targetRoute = `${normalizedTargetPath}#${sectionId}`;
        startPageTransitionV2(() => {
          navigate(targetRoute);
        });
        return;
      }

      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });

        setActiveSection(sectionId);

        setTimeout(() => {
          window.scrollBy(0, 1);
          setTimeout(() => window.scrollBy(0, -1), 50);
        }, 300);

        if (window.location.hash !== `#${sectionId}`) {
          navigate(`${normalizedCurrentPath}#${sectionId}`, { replace: true });
        }
      }
    },
    [location.pathname, navigate, startPageTransitionV2, setActiveSection]
  );

  useEffect(() => {
    const hash = location.hash.replace('#', '');

    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);

        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
          setActiveSection(hash);

          setTimeout(() => {
            window.scrollBy(0, 1);
            setTimeout(() => window.scrollBy(0, -1), 50);
          }, 300);
        }
      }, 300);
    } else {
      setActiveSection(null);
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
  }, [location.hash, location.pathname]);

  useEffect(() => {
    const currentPathnameNorm =
      location.pathname !== '/' && location.pathname.endsWith('/')
        ? location.pathname.slice(0, -1)
        : location.pathname;
    const currentParentPath = getParentPath(currentPathnameNorm);
    const previousParentPath = getParentPath(prevPathnameRef.current);

    let autoExpandedMenuTarget = null;
    if (currentParentPath === ROUTES.ABOUT_US.ROOT)
      autoExpandedMenuTarget = 'About Us';
    else if (currentParentPath === ROUTES.STRATEGIES.ROOT)
      autoExpandedMenuTarget = 'Strategies';
    else if (currentParentPath === ROUTES.HOW_TO_JOIN.ROOT)
      autoExpandedMenuTarget = 'How to Join';
    else if (currentParentPath === ROUTES.LEGAL.ROOT)
      autoExpandedMenuTarget = 'Legal';
    else if (currentPathnameNorm === ROUTES.LIBRARY)
      autoExpandedMenuTarget = 'Library';

    if (currentParentPath !== previousParentPath) {
      setExpandedMenu(autoExpandedMenuTarget);
      setUserHasToggledMenu(false);
    } else {
      if (
        !userHasToggledMenu &&
        autoExpandedMenuTarget &&
        expandedMenu !== autoExpandedMenuTarget
      ) {
        setExpandedMenu(autoExpandedMenuTarget);
      }
    }

    setIsMobileMenuOpen(false);
    prevPathnameRef.current = currentPathnameNorm;
  }, [location.pathname, userHasToggledMenu, expandedMenu]);

  const handleMenuToggle = useCallback(title => {
    setExpandedMenu(prev => {
      const newMenu = prev === title ? null : title;
      setUserHasToggledMenu(true);
      return newMenu;
    });
  }, []);

  const endPageTransition = useCallback(() => {
    setIsPageTransitioning(false);
  }, []);

  const handleDesktopMenuToggle = useCallback(itemId => {
    setExpandedDesktopItemId(prev => (prev === itemId ? null : itemId));
  }, []);

  const value = {
    expandedMenu,
    setExpandedMenu: handleMenuToggle,
    handleMenuToggle,
    userHasToggledMenu,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    activeSection,
    setActiveSection,
    isMainRoute,
    isPageTransitioning,
    endPageTransition,
    pendingNavigationAction,
    setPendingNavigationAction,
    activeNavId,
    setActiveNavId,
    expandedDesktopItemId,
    setExpandedDesktopItemId,
    handleDesktopMenuToggle,
    startPageTransition: startPageTransitionV2,
    navigateToSection, // New helper for scroll navigation
    getParentPath
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

NavigationProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

export default NavigationContext;
